
import { reportsActions } from "@/store/reports/types";
import { defineComponent, PropType } from "vue";
import { WorkflowState } from "@/models/workflow-state";
import { mapActions, mapGetters } from "vuex";
import { RootGetters } from "@/store/getters";
import { RootActions } from "@/store/actions";
import { Button } from '@progress/kendo-vue-buttons';
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: "WorkflowStateButton",
  components: {
    kbutton: Button,
  },
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  data() {
    return {
      isValidWorkflowState: true,
      isHidden: false,
      isLoading: false,
    }
  },
  props: {
    isEnabled: {
      type: Boolean,
      default: true
    },
    currentWorkflowState: {
      type: String as PropType<WorkflowState>,
      required: true,
      default: WorkflowState.NOT_SET
    },
    id: {
      type: String,
      required: true
    },
    objectType: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      isFirstRead: RootGetters.IS_FIRST_READ,
      activeSlide: RootGetters.ACTIVE_SLIDE,
      activeCase: RootGetters.GET_ACTIVE_CASE,
    }),
    buttonText(): string | undefined {
      switch (this.currentWorkflowState) {
        case WorkflowState.NOT_SET:
        case WorkflowState.INCOMPLETE: {
          return this.t('workflowStateButton.incomplete');
        }
        case WorkflowState.COMPLETE: {
          return this.t('workflowStateButton.complete');
        }
        default: {
          console.error("invalid workflow state");
          this.isHidden = true;
        }
      }
    },
    lockIconState(): string {
      if (this.isLoading) {
        return '';
      }
      switch (this.currentWorkflowState) {
        case WorkflowState.NOT_SET:
        case WorkflowState.INCOMPLETE: {
          return `unlock`;
        }
        case WorkflowState.COMPLETE: {
          return `lock`;
        }
      }
    },
    displayButton(): boolean {
      switch (this.objectType) {
        case 'slide':
          return this.isSlideStatusButtonDisplayed
        case 'case':
          return this.isCaseStatusButtonDisplayed
        default:
          return false
      }
    },
    isSlideStatusButtonDisplayed(): boolean {
      return this.isFirstRead && this.activeSlide && this.activeSlide.workflowState;
    },
    isCaseStatusButtonDisplayed(): boolean {
      return this.isFirstRead && this.activeCase && this.activeCase.workflowState;
    },
  },
  methods: {
    ...mapActions({
      handleSlideWorkflowState: RootActions.HANDLE_SLIDE_WORKFLOW_STATE_CHANGE,
      setCaseWorkflowState: RootActions.SET_CASE_WORKFLOW_STATE,
    }),
    ...mapActions('reportsModule', {
      setSlideComplete: reportsActions.SET_SLIDE_COMPLETE,
      updateCaseCompleteness: reportsActions.UPDATE_CASE_COMPLETENESS,
    }),
    calcNewWorkflowState(currentWorkflowState: WorkflowState): WorkflowState {
      switch (currentWorkflowState) {
        case WorkflowState.COMPLETE: {
          return WorkflowState.INCOMPLETE;
        }
        case WorkflowState.NOT_SET:
        case WorkflowState.INCOMPLETE: {
          return WorkflowState.COMPLETE;
        }
        default: {
          console.error("invalid workflow state");
        }
      }
    },
    async handleWorkflowStateChange() {
      if (this.isLoading) return;
      this.isLoading = true;
      let newWorkflowState = this.calcNewWorkflowState(this.currentWorkflowState);
      if (this.objectType === 'slide') {
        await this.setNewSlideWorkflowState(newWorkflowState);
        this.$emit('workflowStateChange', {
          isComplete: newWorkflowState === WorkflowState.COMPLETE
        });
        this.isLoading = false;
      } else if (this.objectType === 'case') {
        await this.setNewCaseWorkflowState(newWorkflowState);
        this.$emit('workflowStateChange', {
          isComplete: newWorkflowState === WorkflowState.COMPLETE
        });
        this.isLoading = false;
      } else {
        console.error("invalid object type");
        this.isLoading = false;
      }
    },
    async setNewSlideWorkflowState(newWorkflowState: WorkflowState): Promise<any> {
      await this.handleSlideWorkflowState({ id: this.id, workflowState: newWorkflowState });
      switch (newWorkflowState) {
        case WorkflowState.COMPLETE:
          await this.setSlideComplete({ isComplete: true })
          break;
        case WorkflowState.INCOMPLETE:
        case WorkflowState.NOT_SET:
        default:
          this.setSlideComplete({ isComplete: false })
          break;
      }
    },
    async setNewCaseWorkflowState(newWorkflowState: WorkflowState): Promise<any> {
      this.setCaseWorkflowState({ id: this.id, workflowState: newWorkflowState });
      this.updateCaseCompleteness({
        caseId: this.id,
        isComplete: newWorkflowState === WorkflowState.COMPLETE
      })
    },
  },
  emits: {
    workflowStateChange(payload: {isComplete: boolean}):boolean {
      return (
        payload.isComplete === true || payload.isComplete === false
      )
    },
  },
})

